import { useState } from "react"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"
import {
  Tree,
  TreeItem,
  TreeItemTrigger,
  TreeItemIndicator,
  TreeItemIcon,
  TreeItemLabel,
  TreeItemContent,
} from "@/components/ui/tree";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { ChevronsDownUp, ChevronsUpDown, Plus } from "lucide-react";
import type { ReportTreeItemData, ReportType } from "@/lib/report-tree";
import {
  getAllFolderIds,
  countReportsInFolder,
  getReportTypesWithReports,
  REPORT_TYPE_ORDER,
} from "@/lib/report-tree";
import type { ReportList } from "@/types/report-list";
import { getReportMenuItems, getFolderMenuItems } from "@/lib/context-menu-items";
import type { ContextMenuCommand } from "@/lib/context-menu-items";

interface ReportNodeMenuItemsProps {
  item: ReportTreeItemData;
  t: TFunction;
  openReportLists: ReportList[];
  onAddToReportList: (item: ReportTreeItemData, reportList: ReportList) => void;
  onContextMenuAction: (command: ContextMenuCommand, item: ReportTreeItemData) => void;
}

export function ReportNodeMenuItems({
  item,
  t,
  openReportLists,
  onAddToReportList,
  onContextMenuAction,
}: Readonly<ReportNodeMenuItemsProps>) {
  return (
    <>
      {getReportMenuItems(t).map((menuItem) => {
        if (menuItem.command === "addToList") {
          return (
            <ContextMenuSub key={menuItem.command}>
              <ContextMenuSubTrigger disabled={openReportLists.length === 0}>
                {menuItem.label}
              </ContextMenuSubTrigger>
              <ContextMenuSubContent>
                {openReportLists.map((reportList) => (
                  <ContextMenuItem
                    key={reportList.reportListKey}
                    onSelect={() => onAddToReportList(item, reportList)}
                  >
                    {reportList.name}
                  </ContextMenuItem>
                ))}
              </ContextMenuSubContent>
            </ContextMenuSub>
          );
        }

        return (
          <ContextMenuItem
            key={menuItem.command}
            onClick={() => onContextMenuAction(menuItem.command, item)}
          >
            {menuItem.label}
          </ContextMenuItem>
        );
      })}
    </>
  );
}

interface FolderNodeMenuItemsProps {
  item: ReportTreeItemData;
  t: TFunction;
  onContextMenuAction: (command: ContextMenuCommand, item: ReportTreeItemData) => void;
}

export function FolderNodeMenuItems({ item, t, onContextMenuAction }: Readonly<FolderNodeMenuItemsProps>) {
  return (
    <>
      {getFolderMenuItems(t).map((menuItem) =>
        <ContextMenuItem
          key={menuItem.command}
          onClick={() => onContextMenuAction(menuItem.command, item)}
        >
          {menuItem.label}
        </ContextMenuItem>,
      )}
    </>
  );
}

interface ReportLibraryProps {
  reportTreesByType: Record<ReportType, ReportTreeItemData[]>;
  expandedFoldersByType: Record<ReportType, string[]>;
  onExpandedFoldersByTypeChange: (folders: Record<ReportType, string[]>) => void;
  selectedNode: ReportTreeItemData | null;
  onNodeSelect: (node: ReportTreeItemData | null) => void;
  onContextClick: () => void;
  activeReportList: ReportList | null;
  openReportLists: ReportList[];
  disabled: boolean;
  onAddToList?: (node: ReportTreeItemData, reportList: ReportList) => void;
  onPreviewReport?: (node: ReportTreeItemData) => void;
  onSavePdfReport?: (node: ReportTreeItemData) => void;
  initialReportType?: ReportType;
}

export function ReportLibrary({
  reportTreesByType,
  expandedFoldersByType,
  onExpandedFoldersByTypeChange,
  selectedNode,
  onNodeSelect,
  onContextClick,
  activeReportList,
  openReportLists,
  disabled,
  onAddToList,
  onPreviewReport,
  onSavePdfReport,
  initialReportType,
}: Readonly<ReportLibraryProps>) {
  const { t } = useTranslation()
  const [searchTerm, setSearchTerm] = useState("");

  const visibleReportTypes = getReportTypesWithReports(reportTreesByType)
  const [activeReportType, setActiveReportType] = useState<ReportType>(
    () => initialReportType ?? visibleReportTypes[0] ?? "project",
  )
  // Fall back to the first visible tab if the active one no longer has reports
  const currentReportType = visibleReportTypes.includes(activeReportType)
    ? activeReportType
    : (visibleReportTypes[0] ?? "project")

  const currentExpandedFolders = expandedFoldersByType[currentReportType] ?? []

  const setCurrentExpandedFolders = (folders: string[]) => {
    onExpandedFoldersByTypeChange({
      ...expandedFoldersByType,
      [currentReportType]: folders,
    })
  }

  const handleTabChange = (value: string) => {
    setActiveReportType(value as ReportType)
    setSearchTerm("")
  }

  const reportTree = reportTreesByType[currentReportType] ?? []

  // Filter out invalid reports (missing names)
  const filterInvalidReports = (nodes: ReportTreeItemData[]): ReportTreeItemData[] => {
    return nodes.reduce<ReportTreeItemData[]>((acc, node) => {
      // Skip invalid reports
      if (
        node.type === "file" &&
        (!node.name || node.name === "ReportName not found" || node.name.trim() === "")
      ) {
        return acc
      }

      // Recursively filter children
      const filteredChildren = node.children ? filterInvalidReports(node.children) : undefined

      acc.push({
        ...node,
        children: filteredChildren,
      })

      return acc
    }, [])
  }

  // Filter tree by search term
  const filterNodes = (nodes: ReportTreeItemData[]): ReportTreeItemData[] => {
    return nodes.reduce<ReportTreeItemData[]>((acc, node) => {
      const matchesSearch = node.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
      const filteredChildren = node.children ? filterNodes(node.children) : []

      if (matchesSearch || filteredChildren.length > 0) {
        acc.push({
          ...node,
          children:
            filteredChildren.length > 0 ? filteredChildren : node.children,
        })
      }

      return acc
    }, [])
  }

  // First filter out invalid reports, then apply search filter
  const validReportTree = filterInvalidReports(reportTree)
  const filteredTree = searchTerm.trim() ? filterNodes(validReportTree) : validReportTree

  // Auto-expand folders when searching
  const searchExpandedFolders = searchTerm.trim()
    ? getAllFolderIds(filteredTree)
    : currentExpandedFolders

  const handleExpandAll = () => {
    setCurrentExpandedFolders(getAllFolderIds(reportTree));
  };

  const handleCollapseAll = () => {
    setCurrentExpandedFolders([]);
  };

  const handleAddToList = (node: ReportTreeItemData) => {
    if (onAddToList && activeReportList) {
      onAddToList(node, activeReportList)
    }
  }

  const handleAddToReportList = (
    node: ReportTreeItemData,
    reportList: ReportList,
  ) => {
    onAddToList?.(node, reportList)
  }

  const handleContextMenuAction = (
    command: string,
    node: ReportTreeItemData,
  ) => {
    if (command === "expandAll") {
      setCurrentExpandedFolders(getAllFolderIds([node]))
    } else if (command === "collapseAll") {
      // Remove all children folder IDs from expanded list
      const childIds = getAllFolderIds(node.children || [])
      setCurrentExpandedFolders(
        currentExpandedFolders.filter(
          (id) => !childIds.includes(id) && id !== node.id,
        ),
      )
    } else if (command === "addToList") {
      handleAddToList(node)
    } else if (command === "preview") {
      onPreviewReport?.(node)
    } else if (command === "savePdf") {
      onSavePdfReport?.(node)
    }
  }

  const renderTreeItems = (items: ReportTreeItemData[]): React.ReactNode => {
    return items.map((item) => (
      <TreeItem key={item.id} id={item.id} value={item}>
        <ContextMenu>
          <ContextMenuTrigger>
            <TreeItemTrigger>
              <TreeItemIndicator />
              <TreeItemIcon />
              <TreeItemLabel>{item.name}</TreeItemLabel>
              {/* Folder: show report count; Report: show "+" add button */}
              {item.type === "folder" && (
                <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                  {countReportsInFolder(item)}
                </span>
              )}
              {item.type === "file" && activeReportList && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="ml-auto h-6 w-6 shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddToList(item);
                        }}
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t("reportLibrary.tooltips.addToList")}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </TreeItemTrigger>
          </ContextMenuTrigger>
          <ContextMenuContent>
            {item.type === "file" ? (
              <ReportNodeMenuItems
                item={item}
                t={t}
                openReportLists={openReportLists}
                onAddToReportList={handleAddToReportList}
                onContextMenuAction={handleContextMenuAction}
              />
            ) : (
              <FolderNodeMenuItems
                item={item}
                t={t}
                onContextMenuAction={handleContextMenuAction}
              />
            )}
          </ContextMenuContent>
        </ContextMenu>
        {item.children && (
          <TreeItemContent>{renderTreeItems(item.children)}</TreeItemContent>
        )}
      </TreeItem>
    ));
  };

  return (
    <div className="flex h-full flex-col" onMouseDown={onContextClick}>
      {/* Header */}
      <div className="flex items-center justify-between border-b px-3 py-2">
        <span className="text-sm font-medium">{t("reportLibrary.title")}</span>
      </div>

      {/* Report type tabs (only shown when more than one type has reports) */}
      {visibleReportTypes.length > 1 && (
        <div className="border-b px-3 py-2">
          <Tabs value={currentReportType} onValueChange={handleTabChange}>
            <TabsList>
              {REPORT_TYPE_ORDER.filter((type) =>
                visibleReportTypes.includes(type),
              ).map((type) => (
                <TabsTrigger key={type} value={type}>
                  {t(`reportLibrary.tabs.${type}`)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      )}

      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Tree panel */}
        <div className="@container flex min-h-0 min-w-0 flex-1 flex-col">
          {/* Toolbar */}
          <div className="flex flex-shrink-0 flex-wrap items-center gap-2 border-b px-3 py-2">
            <Input
              placeholder={t("reportLibrary.search")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-8 min-w-0 flex-1 basis-40"
            />
            <div className="flex shrink-0 items-center gap-2">
              <Button
                onClick={handleExpandAll}
                variant="outline"
                size="sm"
                aria-label={t("reportLibrary.expandAll")}
                title={t("reportLibrary.expandAll")}
                className="shrink-0"
              >
                <ChevronsUpDown className="h-4 w-4" />
                <span className="hidden @[22rem]:inline">
                  {t("reportLibrary.expandAll")}
                </span>
              </Button>
              <Button
                onClick={handleCollapseAll}
                variant="outline"
                size="sm"
                aria-label={t("reportLibrary.collapseAll")}
                title={t("reportLibrary.collapseAll")}
                className="shrink-0"
              >
                <ChevronsDownUp className="h-4 w-4" />
                <span className="hidden @[22rem]:inline">
                  {t("reportLibrary.collapseAll")}
                </span>
              </Button>
            </div>
          </div>
          {/* Tree */}
          <div className="min-h-0 flex-1 overflow-hidden">
             <ScrollArea className="h-full [&_[data-slot=scroll-area-viewport]>div]:!block">
              <div className={disabled ? "pointer-events-none opacity-60" : ""}>
                <Tree
                  selected={selectedNode?.id}
                  onSelect={(_, item) =>
                    onNodeSelect(item as ReportTreeItemData)
                  }
                  expanded={searchExpandedFolders}
                  onExpandedChange={setCurrentExpandedFolders}
                >
                  {renderTreeItems(filteredTree)}
                </Tree>
              </div>
            </ScrollArea>
          </div>
        </div>
      </div>
    </div>
  );
}
